import cs from 'classnames';
import TruncateMarkup from 'react-truncate-markup';
import { format, parseISO } from 'date-fns';
import { ARTICLE_PUBLISHED_DATE_FORMAT } from '../../constants';
import { ARTICLE_URL } from '@constants';
import { Article, ArticleVariant } from '@repo/types';
import { formatTag } from '../../../utils/formatTag';
import { Image, ImageVariant } from '../../../common/components/Image';
import { Link } from 'react-router';

type ArticleCardBaseProps = {
  showAuthor?: boolean;
  showImage?: boolean;
  showTag?: boolean;
  showDate?: boolean;
  showSummary?: boolean;
  variant?: ArticleVariant;
  prioritize?: boolean;
  style?: React.CSSProperties;
};

type ArticleCardProps = ArticleCardBaseProps & {
  article: Article;
};

export function ArticleCard({
  article,
  variant = ArticleVariant.Default,
  showAuthor = false,
  showImage = true,
  showTag = true,
  showDate = true,
  showSummary = false,
  style,
}: ArticleCardProps) {
  const { title, publishDate, imageSrc, author, tags, summary, slug } = article;

  const publishDateFormatted = publishDate ? format(parseISO(publishDate), ARTICLE_PUBLISHED_DATE_FORMAT) : '';
  const className = cs('article-card', {
    'article-card--no-image': !showImage,
    'article-card--no-tag': !showTag,
    'article-card--no-date': !showDate,
    'article-card--default': variant === ArticleVariant.Default,
    'article-card--text-side-by-side': variant === ArticleVariant.TextSideBySide,
  });

  return (
    <article style={style} className={className}>
      <Link className="article-card__link-wrapper" to={`${ARTICLE_URL}/${slug}`}>
        {imageSrc && showImage && (
          <div className="article-card__media-wrapper">
            <Image className="article-card__thumbnail" variant={ImageVariant.Hero} src={imageSrc} alt={title} />
          </div>
        )}
        <div className="article-card__content">
          <div className="article-card__tag-date-wrapper">
            {tags.length > 0 && tags[0] && showTag && <span className="article-card__tag">{formatTag(tags[0])}</span>}
            {showDate && (
              <time className="article-card__date" dateTime={publishDate}>
                {publishDateFormatted}
              </time>
            )}
          </div>
          <TruncateMarkup lines={2}>
            <h2 className="article-card__title">{title}</h2>
          </TruncateMarkup>
          {author && showAuthor && (
            <footer className="article-card__footer">
              <p className="article-card__author">
                <span className="article-card__author-label">by </span>
                <span className="article-card__author-value">{author}</span>
              </p>
            </footer>
          )}
          {summary && showSummary && <p className="article-card__summary">{summary}</p>}
        </div>
      </Link>
    </article>
  );
}
